/**
 * Typed API client.
 *
 * `hc<AppType>` builds a client whose shape comes from the Worker's actual
 * route chain (apps/worker/src/routes.ts), so renaming a route or changing a
 * response body fails `bun run typecheck` here instead of at runtime in a
 * browser. The import is type-only — no Worker code reaches the bundle.
 *
 *   api.api.health.$get()   ->  GET /api/health
 *   api.api.me.$get()       ->  GET /api/me
 *   api.api.links.$get({ query: { q } })  ->  GET /api/links?q=…
 *
 * The client returns a Response rather than throwing, so `unwrap` is where
 * non-2xx becomes an ApiError.
 */

import type { AppType } from '@satisa-memory/worker/routes'
import { hc } from 'hono/client'

// Same-origin: in production the Worker serves the SPA, and in dev Vite
// proxies /api to `wrangler dev` (see vite.config.ts).
export const api = hc<AppType>('/')

/**
 * A failed API call. `status` is the HTTP status, so callers can branch on the
 * cases that mean something specific here:
 *   401 — no valid session; the identity loader sends the browser to sign in.
 *   503 — this deployment has no GitHub app / SIGNING_SECRET configured yet.
 *   0   — offline: the request never reached the Worker, and the service
 *         worker had no saved copy (see src/sw/policy.ts, OFFLINE_BODY).
 */
export const OFFLINE_MESSAGE = "Offline — this isn't saved on this device yet."

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface JsonResponse<T> {
  ok: boolean
  status: number
  statusText: string
  json(): Promise<T>
}

/** The success body of a client response: every `{ error }` variant removed. */
type SuccessBody<R> =
  R extends JsonResponse<infer B> ? Exclude<B, { readonly error: string }> : never

/** Await a client call, throwing ApiError on any non-2xx response. */
export async function unwrap<R extends JsonResponse<unknown>>(
  call: Promise<R>
): Promise<SuccessBody<R>> {
  let res: R
  try {
    res = await call
  } catch {
    // fetch rejects only when the request got no reply at all.
    throw new ApiError(0, OFFLINE_MESSAGE)
  }
  if (!res.ok) {
    // The Worker answers JSON on every /api/* path including 404s, but a
    // misconfigured route can still hand back the SPA's HTML — don't let a
    // parse failure mask the real status.
    const body = (await res.json().catch(() => null)) as {
      error?: string
      offline?: boolean
    } | null
    if (body?.offline === true) throw new ApiError(0, OFFLINE_MESSAGE)
    throw new ApiError(res.status, body?.error || `${res.status} ${res.statusText}`)
  }
  return (await res.json()) as SuccessBody<R>
}
